import { AccessiblePromise } from './AccessiblePromise';
import { FlagResolution } from './FlagResolution';
import { FetchBuilder } from './fetch-util';
import { ResolveFlagsRequest, ResolveFlagsResponse, ApplyFlagsRequest, } from './generated/confidence/flags/resolver/v1/api';
import { LibraryTraces_Library, LibraryTraces_Trace_RequestTrace_Status as TraceStatus, LibraryTraces_TraceId, Monitoring, } from './generated/confidence/telemetry/v1/telemetry';
const FLAG_PREFIX = 'flags/';
const retryCodes = new Set([408, 502, 503, 504]);
export class ResolveError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
    }
}
export class PendingResolution extends AccessiblePromise {
    #context;
    #controller;
    get signal() {
        return this.#controller.signal;
    }
    constructor(promise, context, controller, rejected) {
        super(promise, rejected);
        this.#context = context;
        this.#controller = controller;
    }
    chain(value, rejected) {
        return new PendingResolution(value, this.#context, this.#controller, rejected);
    }
    get context() {
        return this.#context;
    }
    abort() {
        this.#controller.abort();
    }
    then(onfulfilled, onrejected) {
        return super.then(onfulfilled, onrejected);
    }
    catch(onrejected) {
        return super.catch(onrejected);
    }
    finally(onfinally) {
        return super.finally(onfinally);
    }
    static create(context, executor) {
        const controller = new AbortController();
        return new PendingResolution(executor(controller.signal), context, controller);
    }
}
export class FetchingFlagResolverClient {
    fetchImplementation;
    clientSecret;
    sdk;
    applyDebounce;
    resolveTimeout;
    baseUrl;
    traceConsumer;
    waitUntil;
    cacheReadThrough;
    constructor({ fetchImplementation, clientSecret, sdk, applyDebounce, resolveTimeout, 
    // todo refactor to move out environment
    environment, region, resolveBaseUrl, telemetry, logger, waitUntil, cacheProvider, }) {
        this.traceConsumer = telemetry.registerLibraryTraces({
            library: LibraryTraces_Library.LIBRARY_CONFIDENCE,
            version: sdk.version,
            id: LibraryTraces_TraceId.TRACE_ID_RESOLVE_LATENCY,
        });
        const fetchBuilder = new FetchBuilder();
        withTelemetryData(fetchBuilder, telemetry);
        if (environment === 'client') {
            withRequestLogic(fetchBuilder, logger);
        }
        this.fetchImplementation = fetchBuilder.build(fetchImplementation);
        this.clientSecret = clientSecret;
        this.sdk = sdk;
        this.applyDebounce = applyDebounce;
        if (resolveBaseUrl) {
            this.baseUrl = `${resolveBaseUrl}/v1`;
        }
        else {
            this.baseUrl = region ? `https://resolver.${region}.confidence.dev/v1` : 'https://resolver.confidence.dev/v1';
        }
        this.resolveTimeout = resolveTimeout;
        this.waitUntil = waitUntil;
        if (cacheProvider) {
            this.cacheReadThrough = (context, supplier) => {
                const cache = cacheProvider(this.clientSecret);
                let isFromCache = true; // Default to true, will be set to false if supplier is called
                // Create a wrapper supplier that sets the flag when called
                const wrappedSupplier = async () => {
                    isFromCache = false;
                    return supplier();
                };
                return cache.get(context, wrappedSupplier).then(response => {
                    return { response, isFromCache };
                });
            };
        }
        else {
            this.cacheReadThrough = (_context, supplier) => supplier().then(response => ({ response, isFromCache: false }));
        }
    }
    markLatency(latency, status) {
        this.traceConsumer({
            requestTrace: {
                millisecondDuration: Math.round(latency),
                status,
            },
        });
    }
    resolve(context) {
        const request = {
            clientSecret: this.clientSecret,
            evaluationContext: context,
            apply: false,
            sdk: this.sdk,
            flags: [],
        };
        return PendingResolution.create(context, signal => {
            const signalWithTimeout = withTimeout(signal, this.resolveTimeout, new ResolveError('TIMEOUT', 'Resolve timeout'));
            const start = performance.now();
            return this.cacheReadThrough(context, () => this.resolveFlagsJson(request, signalWithTimeout))
                .then(({ response, isFromCache }) => {
                const latency = performance.now() - start;
                this.markLatency(latency, isFromCache ? TraceStatus.STATUS_CACHED : TraceStatus.STATUS_SUCCESS);
                return FlagResolution.ready(context, response, this.createApplier(response.resolveToken));
            })
                .catch(error => {
                const latency = performance.now() - start;
                const errorCode = error instanceof ResolveError ? error.code : 'GENERAL';
                this.markLatency(latency, errorCode === 'TIMEOUT' ? TraceStatus.STATUS_TIMEOUT : TraceStatus.STATUS_ERROR);
                return FlagResolution.failed(context, errorCode, error.message);
            });
        });
    }
    createApplier(resolveToken) {
        const applied = new Set();
        const pending = [];
        let [nextFlush, resolveNextFlush] = resolvablePromise();
        const flush = () => {
            const resolveCurrentFlush = resolveNextFlush;
            [nextFlush, resolveNextFlush] = resolvablePromise();
            timeoutId = 0;
            this.apply({
                flags: pending.splice(0, pending.length),
                clientSecret: this.clientSecret,
                resolveToken,
                sdk: this.sdk,
                sendTime: new Date(),
            }).finally(resolveCurrentFlush);
        };
        let timeoutId = 0;
        return (flagName) => {
            if (applied.has(flagName))
                return;
            this.waitUntil?.(nextFlush);
            applied.add(flagName);
            pending.push({
                flag: FLAG_PREFIX + flagName,
                applyTime: new Date(),
            });
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            if (this.applyDebounce === 0) {
                flush();
            }
            else {
                timeoutId = Number(setTimeout(flush, this.applyDebounce));
            }
        };
    }
    async apply(request) {
        const resp = await this.fetchImplementation(`${this.baseUrl}/flags:apply`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(ApplyFlagsRequest.toJSON(request)),
        });
        if (!resp.ok) {
            throw new Error(`${resp.status}: ${resp.statusText}`);
        }
    }
    async resolveFlagsJson(request, signal) {
        const resp = await this.fetchImplementation(`${this.baseUrl}/flags:resolve`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(ResolveFlagsRequest.toJSON(request)),
            signal,
        });
        if (!resp.ok) {
            throw new Error(`${resp.status}: ${resp.statusText}`);
        }
        const abortPromise = new Promise((_resolve, reject) => {
            if (signal.aborted) {
                reject(signal.reason);
                return;
            }
            signal.addEventListener('abort', () => {
                reject(signal.reason);
            }, { once: true });
        });
        // we've seen issues where Node doesn't properly reject response.json() on abort,
        // so we do it ourselves
        const json = await Promise.race([abortPromise, resp.json()]);
        return ResolveFlagsResponse.fromJSON(json);
    }
}
export function withTelemetryData(fetchBuilder, telemetry) {
    fetchBuilder.modifyRequest(({ headers }) => {
        const monitoring = telemetry.getSnapshot();
        if (monitoring.libraryTraces.length > 0) {
            const base64Message = btoa(String.fromCharCode(...Monitoring.encode(monitoring).finish()));
            return { headers: { ...headers, ['X-CONFIDENCE-TELEMETRY']: base64Message } };
        }
        return {};
    });
}
export function withRequestLogic(fetchBuilder, logger) {
    const fetchResolve = new FetchBuilder()
        // infinite retries without delay until aborted by timeout
        .compose(next => async (request) => {
        try {
            const response = await next(request);
            return response;
        }
        catch (error) {
            logger.error?.(`Confidence: ${error}`);
            throw error;
        }
    })
        .rejectNotOk()
        .retry()
        .rejectOn(response => retryCodes.has(response.status))
        .rateLimit(1, { initialTokens: 3, maxTokens: 2 });
    const fetchApply = new FetchBuilder()
        .limitPending(1000)
        .timeout(30 * 60000 /* TimeUnit.MINUTE */)
        .rejectNotOk()
        .retry({ delay: 5 * 1000 /* TimeUnit.SECOND */, backoff: 2, maxDelay: 5 * 60000 /* TimeUnit.MINUTE */, jitter: 0.2 })
        .rejectOn(response => retryCodes.has(response.status))
        .rateLimit(2)
        // update send-time before sending
        .modifyRequest(({ method, body }) => {
        if (method === 'POST' && body) {
            body = JSON.stringify({ ...JSON.parse(body), sendTime: new Date().toISOString() });
            return { body };
        }
        return {};
    });
    fetchBuilder
        .route(url => url.endsWith('flags:resolve'), fetchResolve)
        .route(url => url.endsWith('flags:apply'), fetchApply);
}
function withTimeout(signal, timeout, reason) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(reason), timeout);
    // in Node setTimeout returns an object, with an unref function which will prevent the timeout from keeping the process alive
    if (typeof timeoutId === 'object')
        timeoutId.unref();
    signal.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        controller.abort(signal.reason);
    });
    return controller.signal;
}
function resolvablePromise() {
    let resolve;
    let reject;
    const promise = new Promise((_resolve, _reject) => {
        resolve = _resolve;
        reject = _reject;
    });
    return [promise, resolve, reject];
}
//# sourceMappingURL=FlagResolverClient.js.map